import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, ShieldCheck, AlertTriangle, ArrowLeft, RefreshCcw,
  CheckCircle2, ListChecks, Send, Lock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function Card({ title, icon, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded-2xl border border-white/10 p-4 sm:p-5"
      style={{ background: 'linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
    >
      <header className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="text-sm sm:text-base font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
          {title}
        </h3>
      </header>
      {children}
    </section>
  );
}

function Row({ label, value, testId }) {
  return (
    <div data-testid={testId} className="flex items-baseline justify-between py-1.5 border-b border-white/5 last:border-0">
      <span className="text-[12.5px] text-white/65">{label}</span>
      <span className="text-sm text-white tabular-nums">{value}</span>
    </div>
  );
}

function Badge({ ok, label, testId }) {
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ${
      ok ? 'bg-emerald-400/10 text-emerald-300 border border-emerald-400/30'
         : 'bg-amber-400/10 text-amber-300 border border-amber-400/30'
    }`}>
      {ok ? '✓' : '◔'} {label}
    </span>
  );
}

export default function AdminMailHealthPage() {
  const navigate = useNavigate();
  const [token] = useState(() => localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [health, setHealth] = useState({});
  const [flows, setFlows] = useState({ flows: [] });
  const [logs, setLogs] = useState({ logs: [] });
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (!token) navigate('/admin/login?next=/admin/mail/health', { replace: true });
  }, [token, navigate]);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Mail Health — Admin (intern)';
    const m = document.createElement('meta');
    m.setAttribute('name', 'robots');
    m.setAttribute('content', 'noindex, nofollow, noarchive');
    m.setAttribute('data-admin-mail', '1');
    document.head.appendChild(m);
    return () => {
      document.title = prev;
      m.remove();
    };
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErr('');
    try {
      const hdr = { Authorization: `Bearer ${token}` };
      const [h, f, l] = await Promise.all([
        fetch(`${API}/admin/mail/health`, { headers: hdr }),
        fetch(`${API}/admin/mail/flows`, { headers: hdr }),
        fetch(`${API}/admin/mail/logs?limit=50`, { headers: hdr }),
      ]);
      if (!h.ok) throw new Error(`health ${h.status}`);
      setHealth(await h.json());
      setFlows(await f.json());
      setLogs(await l.json());
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const triggerDryRunTest = async (flow) => {
    setBusy(true);
    setTestResult(null);
    try {
      const r = await fetch(`${API}/admin/mail/dry-run-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ flow, to: 'test+blok2@example.com' }),
      });
      const j = await r.json();
      setTestResult({ flow, ...j });
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (!token) return null;

  const mode = health?.mode || {};
  const counts = health?.counts_7d || {};
  const webhook = health?.webhook || {};
  const digestReady = health?.growth_weekly_digest_mail || {};
  const actions = health?.owner_actions_required || [];

  return (
    <div
      data-testid="admin-mail-health-page"
      className="min-h-screen text-white"
      style={{ background: 'radial-gradient(circle at 20% 0%, #0c2118 0%, #050b09 60%)' }}
    >
      <header className="border-b border-white/10 px-4 sm:px-6 py-4 flex items-center justify-between sticky top-0 backdrop-blur z-10"
              style={{ background: 'rgba(8,16,12,0.85)' }}>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-mail-back-btn"
            onClick={() => navigate('/admin')}
            className="inline-flex items-center gap-1 text-[12.5px] text-white/70 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Admin dashboard
          </button>
          <Mail className="w-4 h-4 text-emerald-300 ml-2" />
          <h1 data-testid="admin-mail-title" className="text-base sm:text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
            Mail Health
          </h1>
        </div>
        <button
          data-testid="admin-mail-refresh-btn"
          onClick={load}
          disabled={loading || busy}
          className="inline-flex items-center gap-1 text-[12.5px] text-white/80 hover:text-white px-3 py-1.5 rounded-lg border border-white/15">
          <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Vernieuwen
        </button>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid gap-5 lg:grid-cols-2">
        {/* MODE BANNER */}
        <div
          data-testid="mail-mode-banner"
          className={`lg:col-span-2 rounded-2xl border p-4 ${
            mode.current_mode === 'dry_run' ? 'border-emerald-400/30 bg-emerald-400/5'
              : mode.current_mode === 'allowlist_live' ? 'border-amber-400/30 bg-amber-400/5'
              : 'border-white/10 bg-white/5'
          }`}>
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4 text-emerald-300" />
            <span className="text-[13px] font-semibold">
              Huidige modus: <span data-testid="mail-current-mode">{mode.current_mode || '—'}</span>
            </span>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <Badge ok={mode.modes?.A_dry_run} label="A · Dry-run" testId="mode-a-dry-run" />
            <Badge ok={mode.modes?.B_allowlist_live} label="B · Allowlist live" testId="mode-b-allowlist" />
            <Badge ok={mode.modes?.C_transactional_live} label="C · Transactional live" testId="mode-c-transactional" />
            <Badge ok={mode.modes?.D_marketing_bulk} label="D · Marketing/bulk" testId="mode-d-marketing" />
          </div>
          <p className="text-[12px] text-white/55 mt-3">{health?.policy_note || ''}</p>
        </div>

        {/* COUNTS + WEBHOOK */}
        <Card title="Mail counts (7d)" icon={<ListChecks className="w-4 h-4 text-emerald-300" />} testId="card-mail-counts">
          <Row label="Totaal logged" value={counts.total ?? 0} testId="count-total" />
          <Row label="Sent" value={counts.sent ?? 0} testId="count-sent" />
          <Row label="Dry-run" value={counts.dry_run_test ?? 0} testId="count-dry-run" />
          <Row label="Send failed" value={counts.send_failed ?? 0} testId="count-failed" />
          <Row label="Quota exceeded" value={counts.quota_exceeded ?? 0} testId="count-quota" />
          <Row label="Rate limited" value={counts.rate_limited ?? 0} testId="count-rate" />
          <Row label="Domain unverified" value={counts.domain_unverified ?? 0} testId="count-domain" />
          <Row label="Not attempted" value={counts.not_attempted ?? 0} testId="count-not-attempted" />
        </Card>

        <Card title="Resend webhook" icon={<ShieldCheck className="w-4 h-4 text-emerald-300" />} testId="card-mail-webhook">
          <Row label="Endpoint" value={webhook.endpoint || '—'} testId="webhook-endpoint" />
          <Row label="Secret configured" value={webhook.secret_configured ? 'ja' : 'nee'} testId="webhook-secret-configured" />
          <Row label="Received events (totaal)" value={webhook.received_events_total ?? 0} testId="webhook-events-total" />
          <Row label="Resend API key" value={mode.resend_api_key_configured ? 'configured' : 'NIET gezet'} testId="mail-resend-api-configured" />
          <Row label="Allowlist (count)" value={mode.allowlist_count ?? 0} testId="mail-allowlist-count" />
        </Card>

        {/* GROWTH DIGEST READINESS */}
        <Card title="Growth weekly digest mail" icon={<Send className="w-4 h-4 text-emerald-300" />} testId="card-mail-growth-digest">
          <Row label="Ready" value={digestReady.ready ? 'ja' : 'wacht op env'} testId="growth-digest-ready" />
          <Row label="Toegestaan in huidige modus" value={digestReady.allowed_in_current_mode ? 'ja' : 'nee'} testId="growth-digest-allowed" />
          <Row label="Transactional" value={digestReady.policy?.transactional ? 'ja' : 'nee'} testId="growth-digest-transactional" />
          <Row label="Owner approval vereist" value={digestReady.policy?.requires_owner_approval ? 'ja' : 'nee'} testId="growth-digest-requires-approval" />
          <Row label="Max frequentie / week" value={digestReady.policy?.max_frequency_per_week ?? 0} testId="growth-digest-max-freq" />
        </Card>

        {/* BLOCKED capabilities */}
        <Card title="Hard blocked capabilities" icon={<Lock className="w-4 h-4 text-rose-300" />} testId="card-mail-blocked">
          <Row label="Marketing mail" value="🛑 NO-GO" testId="blk-marketing" />
          <Row label="Customer blast" value="🛑 NO-GO" testId="blk-customer-blast" />
          <Row label="Provider outreach live" value="🛑 NO-GO" testId="blk-provider-outreach" />
          <Row label="Bulk send" value="🛑 NO-GO" testId="blk-bulk" />
          <Row label="Affiliate promotion" value="🛑 NO-GO" testId="blk-affiliate" />
          <Row label="Secret disclosure in UI" value="🛑 NO-GO" testId="blk-secret-disclosure" />
        </Card>

        {/* OWNER ACTIONS */}
        <Card title="Owner / externe acties" icon={<AlertTriangle className="w-4 h-4 text-amber-300" />} testId="card-mail-owner-actions">
          {actions.length === 0 ? (
            <p data-testid="mail-owner-actions-empty" className="text-[12.5px] text-white/55">Geen openstaande owner-acties.</p>
          ) : (
            <ul data-testid="mail-owner-actions-list" className="space-y-2">
              {actions.map((a) => (
                <li key={a.id} className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <div className="text-[12.5px] font-semibold text-white">{a.title_nl}</div>
                  <div className="text-[11.5px] text-white/55 mt-0.5">{a.where}</div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* FLOW MATRIX */}
        <Card title="Flow matrix" icon={<ListChecks className="w-4 h-4 text-emerald-300" />} testId="card-mail-flows">
          <p className="text-[11.5px] text-white/55 mb-2">Totaal flows: {flows.count} · huidige modus: {flows.current_mode}</p>
          <div data-testid="mail-flows-list" className="space-y-1 max-h-80 overflow-y-auto">
            {(flows.flows || []).map((f) => (
              <div key={f.flow_name} className="flex items-center justify-between border-b border-white/5 py-1">
                <div className="text-[12px] text-white/85">
                  <span className="font-mono">{f.flow_name}</span>
                  {f.transactional ? (
                    <span className="ml-2 text-[10px] text-emerald-300/80">transactional</span>
                  ) : (
                    <span className="ml-2 text-[10px] text-rose-300/80">marketing</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10.5px] ${f.allowed_in_current_mode ? 'text-emerald-300' : 'text-white/40'}`}>
                    {f.allowed_in_current_mode ? 'toegestaan' : 'geblokkeerd'}
                  </span>
                  <button
                    data-testid={`flow-dryrun-btn-${f.flow_name}`}
                    onClick={() => triggerDryRunTest(f.flow_name)}
                    disabled={busy || !f.transactional}
                    className="text-[10.5px] px-2 py-0.5 rounded border border-white/15 text-white/80 hover:text-white disabled:opacity-30">
                    dry-run test
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* RECENT LOGS */}
        <Card title="Laatste 50 mail logs" icon={<ListChecks className="w-4 h-4 text-emerald-300" />} testId="card-mail-logs">
          {(logs.logs || []).length === 0 ? (
            <p data-testid="mail-logs-empty" className="text-[12.5px] text-white/55">Geen logs gevonden.</p>
          ) : (
            <ul data-testid="mail-logs-list" className="space-y-1 max-h-80 overflow-y-auto">
              {(logs.logs || []).slice(0, 50).map((log, i) => (
                <li key={i} className="flex items-center justify-between border-b border-white/5 py-1">
                  <span className="text-[11.5px] font-mono text-white/70">
                    {(log.created_at || '').slice(0, 19)}
                  </span>
                  <span className="text-[11.5px] text-white/85">{log.flow}</span>
                  <span className={`text-[11px] ${
                    log.status === 'sent' || log.status === 'delivered'
                      ? 'text-emerald-300'
                      : log.status === 'dry_run_test'
                      ? 'text-sky-300'
                      : 'text-amber-300'
                  }`}>{log.status}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* TEST RESULT */}
        {testResult && (
          <div data-testid="mail-test-result" className="lg:col-span-2 rounded-xl border border-white/10 p-3 bg-white/5">
            <div className="text-[12.5px] text-white/80">
              Laatste test ({testResult.flow}):{' '}
              <span className="font-mono text-emerald-300">{testResult.result?.status || 'unknown'}</span>
            </div>
          </div>
        )}

        {err && (
          <div data-testid="mail-error" className="lg:col-span-2 rounded-xl border border-rose-400/30 bg-rose-400/10 text-rose-200 p-3 text-[12.5px]">
            {err}
          </div>
        )}
      </main>
    </div>
  );
}
